import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Resolver, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import {
  UserKeys,
  editGroupTemplate,
  getGroupTemplateById,
  saveGroupTemplate,
} from '../../../../api/users-api';
import { GroupTemplate } from '../../../../../types';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import EmptyStateMessage from '../../../../shared/empty-state-message';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import SimpleFormPageNew from '../../../../shared/simple-form-page-new';
import { StepConfig } from '../../../../shared/shared-form-stepper-new';
import { pagesConfig } from '../../../../shared/pages-config';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import {
  GroupTemplateFormData,
  groupTemplateFormDefaultValues,
  toGroupTemplateFormValues,
  toGroupTemplatePayload,
} from './group-template-form-data';
import {
  GroupTemplateBasicInfoSchema,
  GroupTemplateFormSchema,
  GroupTemplatePermissionsSchema,
  GroupTemplateSecuritySchema,
  GroupTemplateSettingsFieldsSchema,
} from './group-template-form-schema';
import BasicInfoStep from './form-steps/basic-info-step';
import SettingsStep from './form-steps/settings-step';
import PermissionsStep from './form-steps/permissions-step';
import SecurityStep from './form-steps/security-step';

export type GroupTemplatePageMode = 'create' | 'edit';

interface GroupTemplateFormLayoutProps {
  title: string;
  pageMode?: GroupTemplatePageMode;
}

const pageModeConfig = {
  create: {
    changeLogRole: DirectoryRoles.CreateGroupTemplate.value,
    successMessage: 'Group Template added successfully!',
    errorMessage:
      'There was an issue adding this group template. Please refresh and try again.',
    analyticsEvent: analytics.directory.group_templates.create_group_template,
  },
  edit: {
    changeLogRole: DirectoryRoles.EditGroupTemplates.value,
    successMessage: 'Group Template edited successfully!',
    errorMessage:
      'There was an issue editing this group template. Please refresh and try again.',
    analyticsEvent: analytics.directory.group_templates.update_group_template,
  },
};

export default function GroupTemplateFormLayout({
  title,
  pageMode = 'create',
}: GroupTemplateFormLayoutProps) {
  const isEdit = pageMode === 'edit';
  const modeConfig = pageModeConfig[pageMode];
  const { id } = useParams();
  const templateId = Number(id);
  const hasValidTemplateId = Number.isInteger(templateId) && templateId > 0;

  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setSnackbar } = useStore();
  const { changeLogRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [stepCompleted, setStepCompleted] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [isTemplateLoaded, setIsTemplateLoaded] = useState(false);

  const {
    data: template,
    isLoading: isLoadingTemplate,
    isError: isTemplateError,
  } = useQuery({
    queryKey: [UserKeys.GroupTemplates, templateId],
    queryFn: () => getGroupTemplateById(templateId),
    enabled: isEdit && hasValidTemplateId,
    // Always load the latest saved template, and never refetch mid-edit.
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<GroupTemplateFormData>({
    resolver: zodResolver(
      GroupTemplateFormSchema
    ) as Resolver<GroupTemplateFormData>,
    defaultValues: groupTemplateFormDefaultValues,
  });

  // Load the saved template into the form once, so later refetches cannot
  // overwrite the user's in-progress changes.
  useEffect(() => {
    if (template && !isTemplateLoaded) {
      reset(toGroupTemplateFormValues(template));
      setIsTemplateLoaded(true);
    }
  }, [template, isTemplateLoaded, reset]);

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const steps: StepConfig[] = useMemo(
    () => [
      {
        id: 'general-info',
        label: 'General Information',
        schema: GroupTemplateBasicInfoSchema,
      },
      {
        id: 'settings',
        label: 'Settings',
        schema: GroupTemplateSettingsFieldsSchema,
      },
      {
        id: 'permissions',
        label: 'Permissions',
        schema: GroupTemplatePermissionsSchema,
      },
      {
        id: 'security',
        label: 'Security Settings',
        schema: GroupTemplateSecuritySchema,
      },
    ],
    []
  );

  const handleCancel = () => {
    navigate(`/${pagesConfig.groupTemplates.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: GroupTemplate) =>
      isEdit
        ? editGroupTemplate(data, changeLogMessage)
        : saveGroupTemplate(data, changeLogMessage),
    onSuccess: (savedTemplate, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: modeConfig.successMessage,
      });

      queryClient.setQueryData<GroupTemplate[]>(
        [UserKeys.GroupTemplates],
        (oldData) => {
          if (isEdit) {
            return oldData?.map((existing) =>
              existing.keyString === data.keyString ? data : existing
            );
          }
          const created = savedTemplate as GroupTemplate;
          return oldData ? [...oldData, created] : [created];
        }
      );

      setChangeLogMessage('');
      bypass();
      reset();
      setActiveStep(0);
      navigate(`/${pagesConfig.groupTemplates.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: modeConfig.errorMessage,
      });
    },
    onSettled: () => {
      createEvent(modeConfig.analyticsEvent, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groupTemplates,
      });
      setOpenChangeLogDialog(false);
    },
  });

  // Build the payload from getValues(), not the resolver's parsed data: the
  // schema strips fields it does not declare, including the id, keyString and
  // createdBy an edit needs to update the existing template.
  const submitTemplate = () => mutate(toGroupTemplatePayload(getValues()));

  const onSubmit = () => {
    if (isAbleToSeeUIElement(changeLogRoles, modeConfig.changeLogRole)) {
      setOpenChangeLogDialog(true);
    } else {
      submitTemplate();
    }
  };

  const isTemplateUnavailable =
    isEdit &&
    (!hasValidTemplateId ||
      isTemplateError ||
      (!isLoadingTemplate && !template));
  const isWaitingForTemplate =
    isEdit && !isTemplateUnavailable && !isTemplateLoaded;

  const renderStep = () => {
    switch (steps[activeStep]?.id) {
      case 'general-info':
        return <BasicInfoStep control={control} errors={errors} />;
      case 'settings':
        return (
          <SettingsStep
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
        );
      case 'permissions':
        return (
          <PermissionsStep
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
          />
        );
      case 'security':
        return (
          <SecurityStep control={control} watch={watch} setValue={setValue} />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (isTemplateUnavailable) {
      return isTemplateError ? (
        <EmptyStateMessage
          title="Unable to load group template"
          message="There was an issue loading this group template. Please refresh and try again."
        />
      ) : (
        <EmptyStateMessage
          title="Group template not found"
          message="This group template may have been deleted."
        />
      );
    }
    if (isWaitingForTemplate) {
      return (
        <Stack gap={3} aria-busy="true" aria-label="Loading group template">
          <Skeleton variant="rounded" width={240} height={24} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      );
    }
    return renderStep();
  };

  return (
    <>
      <SimpleFormPageNew
        title={title}
        trigger={trigger}
        steps={steps}
        content={renderContent()}
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isTemplateUnavailable || isWaitingForTemplate}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        stepCompleted={stepCompleted}
        setStepCompleted={setStepCompleted}
        errors={errors}
      />
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Group Template"
        pageMode={pageMode}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={submitTemplate}
        mutateLoading={isPending}
      />
    </>
  );
}
